// Setup › People & access › KPI visibility (`/c/{slug}/setup/kpi-visibility`; KPI-VISIBILITY,
// founder-approved 2026-10-22; 05 §KPI visibility; managers). Everyone sees the whole chart;
// KPI data — scorecards, priorities, task load, money — is each person's own seat and the branch
// below by default (admins see everything). A grant widens that for a job role, a seat, a team or
// a person: "Account Manager (job role) → Same level + Up my chain, all KPI dimensions".
//   - The grants as rows, each with edit and remove as visible row icons, "Add grant" on top.
//   - "Preview as…": a person and a dimension group; the server resolves which seats they would
//     see (`GET kpi-visibility/preview`, the same resolver every KPI read uses), highlighted on a
//     mini tree of the chart.
// Three calls on load (the grants, the org chart, the people for the picker); job roles load
// with the grant dialog, the preview when a person is picked.
import {
  describeGrant,
  KPI_GROUP_LABELS,
  KPI_GROUPS,
  KPI_SCOPE_LABELS,
  KPI_SCOPES,
  type KpiGroup,
  type KpiScope,
} from "@companyos/domain-work/org";
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  OrgTree,
  SettingsGroup,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToggleGroup,
  ToggleGroupItem,
  toast,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import { peopleQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "./problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "./row-actions";
import { SetupPage } from "./setup-page";

type Grant = OperationOutput<"kpiVisibility.list">["items"][number];
type Chart = OperationOutput<"orgChart.get">;
type Kind = Grant["principal_kind"];

const KIND_LABELS: Record<Kind, string> = {
  role: "job role",
  seat: "seat",
  team: "team",
  person: "person",
};
const KIND_TITLES: Record<Kind, string> = {
  role: "Job role",
  seat: "Seat",
  team: "Team",
  person: "Person",
};
const SCOPE_HINTS: Record<KpiScope, string> = {
  own: "Their own seat",
  branch: "Their seat and everyone below it",
  peers: "Seats under the same manager, and seats of the same job role",
  up: "Their manager, that manager's manager, up to the top",
  everything: "Every seat in the company",
};

const grantsKey = (co: string) => ["company", co, "kpi-visibility"] as const;

export function KpiVisibilityScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const grants = useQuery({
    queryKey: grantsKey(company.id),
    queryFn: () => api.client.kpiVisibility.list({ params: { co: company.id } }),
  });
  const chart = useQuery(orgChartQuery(api, company.id));
  const [editing, setEditing] = useState<Grant | "new" | null>(null);
  const [removing, setRemoving] = useState<Grant | null>(null);
  const rows = grants.data?.items ?? [];
  return (
    <SetupPage
      title="KPI visibility"
      icon={EyeIcon}
      slot="setup-kpi-visibility"
      {...(grants.data ? { count: rows.length } : {})}
      actions={
        <Button size="sm" onClick={() => setEditing("new")} data-slot="kpi-grant-add">
          <PlusIcon data-icon="inline-start" />
          Add grant
        </Button>
      }
    >
      <Notice tone="info">
        Everyone sees the whole chart: seats, people, titles, teams and responsibilities. KPI data
        is each person's own seat and everyone below them; owners and admins see everything. Grants
        add more — they never take anything away.
      </Notice>
      {grants.isPending ? (
        <Loading label="Loading KPI visibility" rows={3} />
      ) : grants.isError ? (
        <LoadError what="KPI visibility" error={grants.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={EyeIcon}
          title="No grants yet"
          description="Everyone sees their own seat and the people below them."
          actions={
            <Button size="sm" onClick={() => setEditing("new")}>
              Add grant
            </Button>
          }
        />
      ) : (
        <Table data-slot="kpi-grants" className={DENSE_TABLE}>
          <TableCaption className="sr-only">KPI visibility grants.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Who</TableHead>
              <TableHead scope="col">Sees</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((g) => {
              const who = g.principal_name ?? `A removed ${KIND_LABELS[g.principal_kind]}`;
              return (
                <TableRow key={g.id} data-grant={g.id} className="group/row">
                  <TableHead scope="row" className="font-medium">
                    <span className="flex flex-wrap items-center gap-1.5">
                      {who}
                      <Badge variant="outline">{KIND_LABELS[g.principal_kind]}</Badge>
                    </span>
                  </TableHead>
                  <TableCell data-slot="kpi-grant-sees">
                    <span aria-hidden={true} className="mr-1.5 text-muted-foreground">
                      →
                    </span>
                    {describeGrant(g)}
                  </TableCell>
                  <TableCell className="w-px text-right">
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit the grant of ${who}`}
                        tip="Edit"
                        onClick={() => setEditing(g)}
                      />
                      <RowAction
                        icon={Trash2Icon}
                        label={`Remove the grant of ${who}`}
                        tip="Remove"
                        onClick={() => setRemoving(g)}
                      />
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {chart.data ? (
        <PreviewAs api={api} company={company} chart={chart.data} />
      ) : chart.isError ? (
        <LoadError what="The org chart" error={chart.error} />
      ) : null}
      {editing && chart.data ? (
        <GrantDialog
          key={editing === "new" ? "new" : editing.id}
          api={api}
          company={company}
          chart={chart.data}
          grant={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {removing ? (
        <RemoveDialog
          api={api}
          company={company}
          grant={removing}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </SetupPage>
  );
}

/** The seat holders the chart knows, by name (a digital employee says so). */
function holdersOf(chart: Chart, people = true) {
  const out = new Map<string, string>();
  for (const s of chart.seats)
    for (const h of s.holders)
      if (h.membership_kind === "person") out.set(h.membership_id, h.display_name);
      else if (!people && h.membership_kind === "digital_employee")
        out.set(h.membership_id, `${workerName(h.display_name)} (digital employee)`);
  return [...out.entries()].sort((a, b) => a[1].localeCompare(b[1]));
}

/**
 * The people to pick: seat holders (digital employees too with `agents`) and the company's
 * active people holding no seat.
 */
function usePeople(api: Api, company: CompanyAccess, chart: Chart, agents: boolean) {
  const listed = useInfiniteQuery(peopleQuery(api, company.id));
  const byId = new Map(holdersOf(chart, !agents));
  for (const p of listed.data?.pages.flatMap((x) => x.data) ?? [])
    if (p.kind === "person" && p.status === "active" && !byId.has(p.id))
      byId.set(p.id, p.display_name);
  return [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
}

function PreviewAs({ api, company, chart }: { api: Api; company: CompanyAccess; chart: Chart }) {
  const id = useId();
  const people = usePeople(api, company, chart, true);
  const [who, setWho] = useState<string>("");
  const [group, setGroup] = useState<KpiGroup>("kpi");
  const preview = useQuery({
    queryKey: [...grantsKey(company.id), "preview", who],
    queryFn: () =>
      api.client.kpiVisibility.preview({
        params: { co: company.id },
        query: { membership_id: who },
      }),
    enabled: who !== "",
  });
  const seen = new Set(preview.data?.groups[group].seat_ids ?? []);
  const held = new Set(preview.data?.held_seat_ids ?? []);
  const live = chart.seats.filter((s) => s.status !== "closed");
  return (
    <SettingsGroup id="preview" title="Preview as…">
      <div data-slot="kpi-preview" className="flex flex-col gap-3 p-4">
        <p className="text-sm text-muted-foreground">
          Pick a person to see whose KPI data they would see, highlighted on the chart.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Label htmlFor={`${id}-who`} className="sr-only">
            Preview as
          </Label>
          <NativeSelect
            id={`${id}-who`}
            data-slot="kpi-preview-person"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            className="w-64 max-md:w-full"
          >
            <NativeSelectOption value="">Choose a person…</NativeSelectOption>
            {people.map(([m, name]) => (
              <NativeSelectOption key={m} value={m}>
                {name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <ToggleGroup
            variant="chip"
            value={[group]}
            onValueChange={(v: unknown[]) => {
              const next = v[0] as KpiGroup | undefined;
              if (next) setGroup(next);
            }}
            aria-label="Dimension group"
          >
            {KPI_GROUPS.map((g) => (
              <ToggleGroupItem key={g} value={g}>
                {KPI_GROUP_LABELS[g]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        {who === "" ? (
          <p className="text-sm text-muted-foreground">
            The highlighted seats are the ones whose numbers the person sees.
          </p>
        ) : preview.isPending ? (
          <Loading label="Resolving" rows={2} />
        ) : preview.isError ? (
          <LoadError what="The preview" error={preview.error} />
        ) : (
          <>
            <p data-slot="kpi-preview-summary" className="text-sm">
              {preview.data.manager
                ? "An owner or admin: sees everything."
                : `Sees ${live.filter((s) => seen.has(s.id)).length} of ${live.length} seats` +
                  (preview.data.grant_ids.length
                    ? ` (${preview.data.grant_ids.length === 1 ? "1 grant applies" : `${preview.data.grant_ids.length} grants apply`}).`
                    : " (no grant applies: their seat and below).")}
            </p>
            <MiniTree seats={live} seen={seen} held={held} />
          </>
        )}
      </div>
    </SettingsGroup>
  );
}

/** The chart as a small top-down tree, the seen seats tinted, the person's own marked. */
function MiniTree({
  seats,
  seen,
  held,
}: {
  seats: Chart["seats"];
  seen: ReadonlySet<string>;
  held: ReadonlySet<string>;
}) {
  const ids = new Set(seats.map((s) => s.id));
  const childrenOf = (parent: string | null) =>
    seats
      .filter((s) =>
        parent === null
          ? s.supervisor_seat_id === null || !ids.has(s.supervisor_seat_id)
          : s.supervisor_seat_id === parent,
      )
      .sort((a, b) => a.title.localeCompare(b.title));
  const node = (s: Chart["seats"][number], depth: number): ReactNode => {
    const kids = depth < 12 ? childrenOf(s.id) : [];
    const on = seen.has(s.id);
    return (
      <li key={s.id}>
        <div
          data-seat={s.id}
          data-seen={on ? "" : undefined}
          className={cn(
            "inline-flex w-40 flex-col rounded-md border px-2 py-1.5 text-left text-xs",
            on
              ? "border-primary bg-primary/10 text-foreground"
              : "border-subtle bg-surface text-muted-foreground",
          )}
        >
          <span className="truncate font-medium">{s.title}</span>
          <span className="truncate">
            {s.holders.map((h) => workerName(h.display_name)).join(", ") || "Open seat"}
            {held.has(s.id) ? " · them" : ""}
          </span>
        </div>
        {kids.length ? <ul>{kids.map((k) => node(k, depth + 1))}</ul> : null}
      </li>
    );
  };
  return (
    <div data-slot="kpi-mini-tree" className="max-h-[420px] overflow-auto rounded-md border p-3">
      <OrgTree view="tree" zoom={0.85}>
        {childrenOf(null).map((s) => node(s, 0))}
      </OrgTree>
    </div>
  );
}

function GrantDialog({
  api,
  company,
  chart,
  grant,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  chart: Chart;
  grant: Grant | null;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const people = usePeople(api, company, chart, false);
  const [kind, setKind] = useState<Kind>(grant?.principal_kind ?? "role");
  const [principal, setPrincipal] = useState(grant?.principal_id ?? "");
  const [scopes, setScopes] = useState<KpiScope[]>([...(grant?.scopes ?? ["peers", "up"])]);
  const [groups, setGroups] = useState<KpiGroup[]>([...(grant?.groups ?? [])]);
  const [touched, setTouched] = useState(false);
  const roles = useQuery({
    queryKey: ["company", company.id, "roles", "all"],
    queryFn: () => api.client.roles.list({ params: { co: company.id }, query: { limit: 200 } }),
    enabled: grant === null,
  });
  const options: [string, string][] =
    kind === "role"
      ? (roles.data?.data ?? []).map((r) => [r.id, r.name])
      : kind === "seat"
        ? chart.seats.filter((s) => s.status !== "closed").map((s) => [s.id, s.title])
        : kind === "team"
          ? chart.teams.map((t) => [t.id, t.name])
          : people;
  const save = useMutation({
    mutationFn: () =>
      grant
        ? api.client.kpiVisibility.update({
            params: { co: company.id, id: grant.id },
            body: { scopes, groups },
          })
        : api.client.kpiVisibility.create({
            params: { co: company.id },
            body: { principal_kind: kind, principal_id: principal, scopes, groups },
          }),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: grantsKey(company.id) });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "org-chart"] });
      toast.add({
        title: `${saved.principal_name ?? "Grant"}: ${describeGrant(saved)}`,
        type: "success",
      });
      onClose();
    },
  });
  const missing = !grant && principal === "";
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!missing && scopes.length > 0) save.mutate();
  };
  const all = groups.length === 0;
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent data-slot="kpi-grant-dialog">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>
              {grant
                ? `Edit the grant of ${grant.principal_name ?? "a removed principal"}`
                : "Add grant"}
            </DialogTitle>
            <DialogDescription>
              Who gets to see more KPI data, and how far: grants add to what everyone already sees.
            </DialogDescription>
          </DialogHeader>
          {grant ? null : (
            <div className="grid grid-cols-[10rem_1fr] gap-3 max-md:grid-cols-1">
              <Field>
                <FieldLabel htmlFor={`${id}-kind`}>Who</FieldLabel>
                <NativeSelect
                  id={`${id}-kind`}
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as Kind);
                    setPrincipal("");
                  }}
                >
                  {(["role", "seat", "team", "person"] as const).map((k) => (
                    <NativeSelectOption key={k} value={k}>
                      {KIND_TITLES[k]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field data-invalid={(touched && missing) || undefined}>
                <FieldLabel htmlFor={`${id}-principal`}>{KIND_TITLES[kind]}</FieldLabel>
                <NativeSelect
                  id={`${id}-principal`}
                  data-slot="kpi-grant-principal"
                  value={principal}
                  aria-invalid={(touched && missing) || undefined}
                  onChange={(e) => setPrincipal(e.target.value)}
                >
                  <NativeSelectOption value="">Choose…</NativeSelectOption>
                  {options.map(([v, label]) => (
                    <NativeSelectOption key={v} value={v}>
                      {label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
            </div>
          )}
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">Sees</legend>
            {KPI_SCOPES.map((s) => (
              <div key={s} className="flex items-start gap-2">
                <Checkbox
                  id={`${id}-scope-${s}`}
                  checked={scopes.includes(s)}
                  onCheckedChange={(on) =>
                    setScopes((prev) =>
                      KPI_SCOPES.filter((x) => (x === s ? on === true : prev.includes(x))),
                    )
                  }
                />
                <Label htmlFor={`${id}-scope-${s}`} className="flex flex-col items-start gap-0">
                  <span>{KPI_SCOPE_LABELS[s]}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {SCOPE_HINTS[s]}
                  </span>
                </Label>
              </div>
            ))}
            {touched && scopes.length === 0 ? (
              <p role="alert" className="text-xs text-danger-text">
                Pick at least one.
              </p>
            ) : null}
          </fieldset>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">For</legend>
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${id}-all`}
                checked={all}
                onCheckedChange={(on) => setGroups(on === true ? [] : ["kpi"])}
              />
              <Label htmlFor={`${id}-all`}>All KPI dimensions</Label>
            </div>
            {all ? null : (
              <div className="flex flex-wrap gap-x-4 gap-y-2 pl-6">
                {KPI_GROUPS.map((g) => (
                  <div key={g} className="flex items-center gap-2">
                    <Checkbox
                      id={`${id}-group-${g}`}
                      checked={groups.includes(g)}
                      onCheckedChange={(on) =>
                        setGroups((prev) => {
                          const next = KPI_GROUPS.filter((x) =>
                            x === g ? on === true : prev.includes(x),
                          );
                          // None picked is not "all": keep the last one; every one is "all".
                          if (next.length === 0) return prev;
                          return next.length === KPI_GROUPS.length ? [] : next;
                        })
                      }
                    />
                    <Label htmlFor={`${id}-group-${g}`}>{KPI_GROUP_LABELS[g]}</Label>
                  </div>
                ))}
              </div>
            )}
            <FieldDescription>
              Money stays behind its own finance access: a grant shows cost summaries, never pay.
            </FieldDescription>
          </fieldset>
          {touched && missing ? (
            <p role="alert" className="text-xs text-danger-text">
              Choose who the grant is for.
            </p>
          ) : null}
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The grant was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" />} disabled={save.isPending}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={save.isPending} data-slot="kpi-grant-save">
              {save.isPending ? "Saving…" : grant ? "Save" : "Add grant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RemoveDialog({
  api,
  company,
  grant,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  grant: Grant;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.client.kpiVisibility.delete({ params: { co: company.id, id: grant.id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: grantsKey(company.id) });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "org-chart"] });
      toast.add({ title: "Grant removed", type: "success" });
      onClose();
    },
  });
  const who = grant.principal_name ?? `a removed ${KIND_LABELS[grant.principal_kind]}`;
  return (
    <Dialog open onOpenChange={(o) => !o && !remove.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove this grant?</DialogTitle>
          <DialogDescription>
            {who} goes back to their own seat and the people below them ({describeGrant(grant)}{" "}
            stops applying).
          </DialogDescription>
        </DialogHeader>
        {remove.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(remove.error, "The grant was not removed.")}
          </Notice>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />} disabled={remove.isPending}>
            Cancel
          </DialogClose>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? "Removing…" : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
